import { appConfig } from "@/constants";
import { createAnalytics } from "@imtbl/react-analytics";

export enum UserJourney {
  FEED_ONBOARDING = "FeedOnboarding",
  FEED_DISCOVER = "FeedDiscover",
  PROFILE = "Profile",
  CLAIM = "Claim",
}

export enum Action {
  LANDED = "Landed",
  WATCHED = "Watched",
  VIEWED = "Viewed",
  OPENED = "Opened",
  PRESSED = "Pressed",
  STARTED = "Started",
  SUCCEEDED = "Succeeded",
  FAILED = "Failed",
  IMPRESSION = "Impression",
  REQUEST = "Request",
  COMPLETED = "Completed",
  EARNED = "Earned",
}

export enum ControlType {
  BUTTON = "Button",
  LINK = "Link",
  IMAGE = "Image",
}

export enum InteractionType {
  PRESS = "Press",
  WATCH = "Watch",
  ANSWER = "Answer",
  PLAY = "Play",
}

export enum TrackObject {
  FEED = "Feed",
  DISCOVER_CONTENT = "DiscoverContent",
  PROFILE = "Profile",
  CLAIM = "Claim",
  STAKING = "Staking",
  LOGIN = "Login",
}

export interface BaseAnalyticsExtras {
  [key: string]: unknown;
  isLoggedIn: boolean | undefined;
  screen: string;
  userJourney: UserJourney;
}

export interface FailedExtras {
  [key: string]: unknown;
}

interface BaseAnalytics {
  action: Action;
  extras: BaseAnalyticsExtras;
}

export type LinkTrackingEvent = {
  action: Action.PRESSED;
  extras: {
    controlType: ControlType.LINK;
    controlLabel: string;
    section: string;
    url: string;
  };
};

export const { AnalyticsProvider, useAnalytics } = createAnalytics<
  string,
  string,
  string,
  string,
  Action
>({
  writeKey: appConfig.SEGMENT_KEY,
  appName: "Play",
});

/**
 * This function is a generic function used as a utility to create utility tracking hooks for different areas of the web app. This hook should not be used directly.
 *
 * @param object - The object to track.
 * @returns A function that can be used to track an event.
 *
 * @see ./discover.ts for a working example of creating the discover specific tracking hook.
 *
 * @example
 * `./discover.ts`
 * // Create a tracking hook for the discover feed
 * export const useDiscoverAnalytics = () => useAnalyticsTracker<DiscoverFeedEvent>(TrackObject.FEED);
 *
 * `DiscoverFeed.tsx`
 * // Track an event for the discover feed by consuming the above tracking hook
 * const { track } = useDiscoverAnalytics();
 *
 * track({
 *   action: Action.VIEWED,
 *   extras: { screen: "Home", userJourney: UserJourney.FEED_DISCOVER },
 * });
 */
export const useAnalyticsTracker = <
  T extends { action: Action; extras: Record<string, unknown> } | BaseAnalytics,
>(
  object: TrackObject,
) => {
  const { trackV3 } = useAnalytics();

  const track = ({ action, extras }: T) => {
    trackV3({
      object,
      action,
      extras,
    });
  };

  return { track };
};
